import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  Toolbar,
  Typography,
  Box,
  Stack,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import { Formik, useFormikContext } from "formik";
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PayPeriodEmployeeReview from "./PayPeriodData/PayPeriodEmployeeReview";
import EmployeePayrollMappingForm from "./PayPeriodData/EmployeePayrollMappingForm";
import { loadPayPeriodSetup, updatePayPeriodSetup, isLock, getFrequencies, getAccounts, PreviousPayrollByCorp, getProfitCenters } from "../../../api";
import { buildUpdatePayPeriodSetupPayload } from "../../../Payloads/EntryPayload";
import { FieldRow, CustomSelect, CustomDatePicker, } from "../../../HelperFunctions/EmployeeFormHelpers";
import UnlockDialog from "../../../Components/UnlockDialog";
import shortcutManager from "../../../Utilities/ShortcutManager";
import { useSnackbar } from "notistack";
import { Loader } from "../../../Components/Loader";
import { Dropdown } from "../../../HelperFunctions/PayrollDropdown";
import PayrollsPopup from "../../../Components/PayrollPopup";

const AutoGenerateName = () => {
  const { values, setFieldValue, initialValues } = useFormikContext();
  const { payFrom, payTo, frequency, payrollName, payDate } = values;

  const prevFrom = React.useRef(payFrom);
  const prevTo = React.useRef(payTo);
  const prevFreq = React.useRef(frequency);
  const prevInitialValues = React.useRef(initialValues);

  useEffect(() => {
    const freqChanged = prevFreq.current !== frequency;
    const fromChanged = prevFrom.current !== payFrom;
    const toChanged = prevTo.current !== payTo;
    const initialValuesChanged = prevInitialValues.current !== initialValues;

    if (initialValuesChanged) {
      prevFrom.current = payFrom;
      prevTo.current = payTo;
      prevFreq.current = frequency;
      prevInitialValues.current = initialValues;
      return;
    }

    // 1. Update Dates on Frequency or Pay Period From Change
    if ((freqChanged || fromChanged) && frequency && payFrom) {
      const start = dayjs(payFrom);
      if (start.isValid()) {
        let newStart = start;
        let newEnd = null;

        switch (frequency) {
          case "Weekly":
            newEnd = newStart.add(6, "day");
            break;
          case "Biweekly":
            newEnd = newStart.add(13, "day");
            break;
          case "Semimonthly":
            if (start.date() <= 15) {
              newStart = start.date(1);
              newEnd = start.date(15);
            } else {
              newStart = start.date(16);
              newEnd = start.endOf("month");
            }
            break;
          case "Monthly":
            newStart = start.startOf("month");
            newEnd = start.endOf("month");
            break;
          case "Quarterly":
            newEnd = start.add(3, "month").subtract(1, "day");
            break;
          case "Annually":
            newEnd = start.add(1, "year").subtract(1, "day");
            break;
          default:
            break;
        }

        if (newEnd) {
          if (newStart.format("YYYY-MM-DD") !== payFrom) {
            setFieldValue("payFrom", newStart.format("YYYY-MM-DD"));
          }
          if (newEnd.format("YYYY-MM-DD") !== payTo) {
            setFieldValue("payTo", newEnd.format("YYYY-MM-DD"));
          }
        }
      }
    }
    // 2. Auto Generate Name
    if ((freqChanged || fromChanged || toChanged) && payFrom && payTo && frequency) {
      const from = dayjs(payFrom);
      const to = dayjs(payTo);
      if (from.isValid() && to.isValid()) {
        const freqChar = frequency.toString().charAt(0).toUpperCase();
        const newName = `${from.format("MM/DD/YYYY")}-${to.format("MM/DD/YYYY")} ${freqChar} Wages`;
        if (payrollName !== newName) {
          setFieldValue("payrollName", newName);
        }

        const isDiffMonth = from.month() !== to.month() || from.year() !== to.year();
        let wasDiffMonth = false;

        if (prevFrom.current && prevTo.current) {
          const prevFromDate = dayjs(prevFrom.current);
          const prevToDate = dayjs(prevTo.current);
          if (prevFromDate.isValid() && prevToDate.isValid()) {
            wasDiffMonth = prevFromDate.month() !== prevToDate.month() || prevFromDate.year() !== prevToDate.year();
          }
        }

        if (isDiffMonth && !wasDiffMonth) {
          setFieldValue("isAccrual", true);
        } else if (!isDiffMonth) {
          setFieldValue("isAccrual", false);
        }
      }
    }

    // 3. Auto Update Pay Date
    if (toChanged && payTo) {
      const end = dayjs(payTo);
      if (end.isValid()) {
        const newPayDate = end.add(5, "day").format("YYYY-MM-DD");
        if (payDate !== newPayDate) {
          setFieldValue("payDate", newPayDate);
        }
      }
    }

    prevFrom.current = payFrom;
    prevTo.current = payTo;
    prevFreq.current = frequency;
  }, [payFrom, payTo, frequency, payrollName, payDate, initialValues, setFieldValue]);



  return null;
};

const PayPeriodSetup = ({ open, onClose, payrollId, corpId, rowData, isAddPayRoll = false, corporationList }) => {


  const [corporationId, setCorporationId] = useState(null);
  const ALL_ID = "000000000000000000000000000000000000";
  const payrollType = isAddPayRoll ? 1 : rowData?.raw?.ImportPayrollType

  const [step, setStep] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [freqLoading, setFreqLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [validAccountIds, setValidAccountIds] = useState(new Set());
  const [unlockPasswordOpen, setUnlockPasswordOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const showErrorSnackbar = (msg) => enqueueSnackbar(msg, { variant: "error" });

  const isReadOnly = Boolean(rowData?.raw?.ReconStatus) || Boolean(rowData?.raw?.ClearedDate);
  const showSuccessSnackbar = (msg) => enqueueSnackbar(msg, { variant: "success" });

  const formikRef = useRef(null);
  const handleUpdateAndNextRef = useRef(null);

  const emptyValues = useMemo(() => {
    const rawPcName = rowData?.profitCenter || rowData?.PCName || "";
    const isMultiple = String(rawPcName).trim().toLowerCase() === "multiple";
    const initialPc = isMultiple ? "" : rawPcName;
    const initialPcId = isMultiple ? "" : (rowData?.profitCenterId || rowData?.PCID || "");
    return {
      corporation: "",
      corporationId: corpId || "",
      profitCenter: initialPc,
      profitCenterId: initialPcId,
      profitCenterName: initialPc,
      unmatchedQueueCalled: false,
      account: "",
      frequency: "",
      payDate: "",
      payFrom: "",
      payTo: "",
      payrollName: "",
      isAccrual: false,
      amount: 0,
      IsHoursDecimal: false,
      frequencyId: "",
      payRollID: "",
      isAddPayRoll: isAddPayRoll,
      wage: [
        { wageName: "", mappedTo: "", coa: "", hrs: "0.00", rate: "0.00", gross: "0.00" }
      ],
      addAndDeductions: [],
      eeAndErTaxes: [],
      importPayrollType: payrollType || 0,
    };
  }, [corpId, isAddPayRoll, payrollType, rowData]);

  const [initialValues, setInitialValues] = useState(emptyValues);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [profitCenterOptions, setProfitCenterOptions] = useState([]);
  const [profitCenterLoading, setProfitCenterLoading] = useState(false);
  const [pcConfirmOpen, setPcConfirmOpen] = useState(false);
  const [pendingUpdateValues, setPendingUpdateValues] = useState(null);
  const initialPcRef = useRef("");
  const emptyValuesRef = useRef(emptyValues);
  emptyValuesRef.current = emptyValues;
  const lastFetchedPayrollIdRef = useRef(null);
  const fetchedFrequenciesRef = useRef(false);
  const lastFetchedAccountsCorpIdRef = useRef(null);
  const lastFetchedPcCorpIdRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  const loading = submitting || setupLoading || freqLoading || accountLoading || profitCenterLoading;


  useEffect(() => {
    if (open) {
      setCorporationId(corpId || null);
    }
  }, [open, corpId]);



  useEffect(() => {
    if (!open) return;

    window.parent.postMessage("HideMenu", "*");

    return () => {
      window.parent.postMessage("ShowMenu", "*");
    };
  }, [open]);

  const toDateString = (value) => {
    if (!value) return "";
    const str = String(value).trim();
    if (str.startsWith("0001")) return "";
    const d = dayjs(value);
    if (!d.isValid()) return "";
    return d.format("YYYY-MM-DD");
  };

  const requiredLabel = (label) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, whiteSpace: "nowrap" }}>
      {label}
      <span style={{ color: "#d32f2f" }}>*</span>
    </span>
  );

  const withFallbackOption = (options, value) => {
    if (!value) return options;
    const exists = options.some(
      (opt) => opt.value === value || opt.label === value
    );
    if (exists) return options;
    return [{ label: value, value }, ...options];
  };

  const handleSetupResponse = useCallback((data) => {
    const corporation = data?.CorporationName ?? "";
    const frequency = data?.PayFrequency || "";
    const isDot = String(data?.AccountName ?? "").trim() === ".";
    const accountId = data?.AccountID ?? "";
    const accountName = isDot ? "" : data?.AccountName ?? "";
    const accountValue = accountId || accountName;

    const corpIdValue = data?.CorporationID || corpId || "";
    if (corpIdValue) {
      setCorporationId((prev) => (prev !== corpIdValue ? corpIdValue : prev));
    }
    const pcId = data?.PCID || data?.ProfitCenterID || data?.ProfitCenterId || "";
    const rawPcName = data?.PCName || data?.ProfitCenterName || data?.ProfitCenter || "";
    const isMultiple = String(rawPcName).trim().toLowerCase() === "multiple";
    const pcName = isMultiple ? "" : rawPcName;
    const effectivePcId = isMultiple ? "" : pcId;
    initialPcRef.current = String(pcName || "");

    const payFromDate = toDateString(data?.PayPeriodFrom);
    const payToDate = toDateString(data?.PayPeriodTo);
    const isDiffMonth = Boolean(
      payFromDate && payToDate &&
      dayjs(payFromDate).isValid() && dayjs(payToDate).isValid() &&
      (dayjs(payFromDate).month() !== dayjs(payToDate).month() || dayjs(payFromDate).year() !== dayjs(payToDate).year())
    );
    const isAccrualValue = isDiffMonth ? (isAddPayRoll ? true : Boolean(data?.IsAccrual)) : false;

    setInitialValues((prev) => ({
      ...prev,
      corporation,
      corporationId: corpIdValue,
      account: accountValue,
      frequency,
      profitCenter: pcName || "",
      profitCenterId: effectivePcId || "",
      profitCenterName: pcName || "",
      payDate: toDateString(data?.PayDate),
      payFrom: payFromDate,
      payTo: payToDate,
      payrollName: data?.PayrollName ?? "",
      isAccrual: isAccrualValue,
      amount: data?.Amount ?? 0.0,
      IsHoursDecimal: data?.IsHoursDecimal ?? false,
      frequencyId: data?.FrequencyID || "",
    }));

    setFrequencyOptions((prev) =>
      withFallbackOption(prev, frequency)
    );

    setAccountOptions((prev) => {
      const currentOptions = prev.length > 0 ? prev : [{ label: "--Select--", value: "", type: "" }];
      if (!accountValue) return currentOptions;

      const exists = currentOptions.some((opt) => opt.value === accountValue);
      if (exists) return currentOptions;

      return [
        { label: accountName || accountValue, value: accountValue },
        ...prev,
      ];
    });
  }, [corpId, isAddPayRoll]);

  const handleSetupResponseRef = useRef(handleSetupResponse);
  handleSetupResponseRef.current = handleSetupResponse;

  useEffect(() => {
    if (!open) {
      lastFetchedPayrollIdRef.current = null;
      setSetupLoading(false);
      return;
    }
    setStep(1);
    setSelectedRow(null);

    if (!payrollId) {
      initialPcRef.current = "";
      setInitialValues(emptyValuesRef.current);
      setFrequencyOptions([]);
      setAccountOptions([]);
      lastFetchedPayrollIdRef.current = null;
      setSetupLoading(false);
      return;
    }

    if (lastFetchedPayrollIdRef.current === payrollId) {
      return;
    }
    lastFetchedPayrollIdRef.current = payrollId;

    const fetchSetup = async () => {
      setSetupLoading(true);
      setLoadError(null);
      try {
        const response = await loadPayPeriodSetup(payrollId);
        if (!openRef.current || lastFetchedPayrollIdRef.current !== payrollId) return;

        const data = response?.PayPeriodSetups?.[0] || {};
        handleSetupResponseRef.current(data);
      } catch (err) {
        if (!openRef.current || lastFetchedPayrollIdRef.current !== payrollId) return;
        lastFetchedPayrollIdRef.current = null;
        setLoadError("Failed to load pay period setup.");
        setInitialValues(emptyValuesRef.current);
      } finally {
        setSetupLoading(false);
      }
    };

    fetchSetup();
  }, [open, payrollId]);

  useEffect(() => {
    if (!open) {
      fetchedFrequenciesRef.current = false;
      setFreqLoading(false);
      return;
    }

    if (fetchedFrequenciesRef.current) return;
    fetchedFrequenciesRef.current = true;

    const fetchFrequencies = async () => {
      setFreqLoading(true);
      try {
        const list = await getFrequencies();
        if (!openRef.current) return;
        let options = (list || []).map((item) => ({
          label: item.label,   // Weekly
          value: item.label,   // Store label in formik
          id: item.value,      // GUID for backend
        }));
        if (options.length > 0) {
          options = [{ label: "--Select--", value: "", id: "" }, ...options];
        }
        setFrequencyOptions(options);
      } catch (err) {
        if (!openRef.current) return;
        fetchedFrequenciesRef.current = false;
      } finally {
        setFreqLoading(false);
      }
    };

    fetchFrequencies();
  }, [open]);

  useEffect(() => {
    if (!initialValues?.frequency) return;
    setFrequencyOptions((prev) =>
      withFallbackOption(prev, initialValues.frequency)
    );
  }, [initialValues?.frequency]);

  const initialAccount = initialValues?.account;
  useEffect(() => {
    if (!open || !corporationId) {
      lastFetchedAccountsCorpIdRef.current = null;
      setAccountLoading(false);
      return;
    }

    if (lastFetchedAccountsCorpIdRef.current === corporationId) {
      return;
    }
    lastFetchedAccountsCorpIdRef.current = corporationId;

    const fetchAccounts = async () => {
      setAccountLoading(true);
      try {
        const list = await getAccounts(corporationId);
        if (!openRef.current || lastFetchedAccountsCorpIdRef.current !== corporationId) return;

        const accountsArray = list?.Accounts || list || [];

        const options = [
          { label: "--Select--", value: "", type: "" },
          ...accountsArray.map((item) => ({
            label: item.Name || item.label,
            value: item.ID || item.value,
            type: item.AccountTypeName || item.type,
          })),
        ];

        setAccountOptions(options);
        setValidAccountIds(
          new Set(options.filter(o => o.value).map(o => o.value))
        );
      } catch (err) {
        if (!openRef.current || lastFetchedAccountsCorpIdRef.current !== corporationId) return;
        lastFetchedAccountsCorpIdRef.current = null;
        console.error(err);
      } finally {
        setAccountLoading(false);
      }
    };

    fetchAccounts();
  }, [open, corporationId]);

  useEffect(() => {
    if (!initialAccount || accountOptions.length === 0) return;
    if (!accountOptions.some((o) => o.value === initialAccount)) {
      const matched = accountOptions.find((opt) => opt.label === initialAccount);
      if (matched) {
        setInitialValues((prev) => ({
          ...prev,
          account: matched.value,  // Store GUID
        }));
      }
    }
  }, [initialAccount, accountOptions]);

  useEffect(() => {
    if (!open || !corporationId) {
      setProfitCenterOptions([]);
      lastFetchedPcCorpIdRef.current = null;
      setProfitCenterLoading(false);
      return;
    }

    if (lastFetchedPcCorpIdRef.current === corporationId) {
      return;
    }
    lastFetchedPcCorpIdRef.current = corporationId;

    const fetchProfitCenters = async () => {
      setProfitCenterLoading(true);
      try {
        const list = await getProfitCenters(corporationId);
        if (!openRef.current || lastFetchedPcCorpIdRef.current !== corporationId) return;

        const options = (list || []).map((item) => {
          const name = item.label || item.Name || item.ProfitCenterName || "";
          const id = item.value || item.ID || item.ProfitCenterId || "";
          return {
            label: name,
            value: name, // map with PCName
            id: id,
          };
        });

        setProfitCenterOptions(options);
      } catch (err) {
        if (!openRef.current || lastFetchedPcCorpIdRef.current !== corporationId) return;
        lastFetchedPcCorpIdRef.current = null;
        console.error("Failed to fetch profit centers:", err);
        setProfitCenterOptions([]);
      } finally {
        setProfitCenterLoading(false);
      }
    };

    fetchProfitCenters();
  }, [open, corporationId]);

  const performUpdate = async (values, isPcUpdate = false) => {
    setSubmitting(true);
    try {
      const payload = buildUpdatePayPeriodSetupPayload(
        values,
        payrollId,
        corporationId,
        frequencyOptions,
        accountOptions,
        isPcUpdate,
        profitCenterOptions
      );

      const response = await updatePayPeriodSetup(payload);
      if (response?.StatusCode === 200 || response?.Status === "Success") {
        formikRef.current?.setFieldValue(
          "payRollID",
          response?.ID || ""
        );
        showSuccessSnackbar("Payperiod setup updated successfully.");
        setStep(2);
      } else {
        showErrorSnackbar(response?.Status || response?.Message || "Failed to update pay period setup.");
      }
    } catch (error) {
      console.error("Update error:", error);
      showErrorSnackbar("An error occurred while updating.");
    } finally {
      setSubmitting(false);
    }
  };

  const triggerUpdateWithPcCheck = async (values) => {
    const currentPc = String(values.profitCenter || values.profitCenterName || "");
    const initialPc = String(initialPcRef.current || "");
    const isPcChanged = Boolean(payrollId) && currentPc !== initialPc;

    if (isPcChanged) {
      setPendingUpdateValues(values);
      setPcConfirmOpen(true);
      setSubmitting(false);
    } else {
      await performUpdate(values, false);
    }
  };

  const handleUpdateAndNext = async (values, { setFieldError, setFieldTouched }) => {

    // 1. Pay Frequency
    if (!values.frequency?.toString().trim()) {
      showErrorSnackbar("Pay Frequency is required.");
      return;
    }
    // 2. Pay Period
    if (!values.payFrom) {
      showErrorSnackbar("Pay Period From date is required.");
      return;
    }
    if (!values.payTo) {
      showErrorSnackbar("Pay Period To date is required.");
      return;
    }
    // Business Rule 1: Pay Period From < Pay Period To
    if (dayjs(values.payFrom).isAfter(dayjs(values.payTo))) {
      showErrorSnackbar("Pay Period From must be before Pay Period To.");
      return;
    }

    // 3. Payroll Name
    if (!values.payrollName?.toString().trim()) {
      showErrorSnackbar("Payroll Name is required.");
      return;
    }
    // 4. Account
    if (!values.account?.toString().trim()) {
      showErrorSnackbar("Account is required.");
      return;
    }
    if (validAccountIds.size > 0 && !validAccountIds.has(values.account)) {
      showErrorSnackbar("Invalid Account. Please select from the available list.");
      return;
    }
    // 5. Pay Date
    if (!values.payDate) {
      showErrorSnackbar("Pay Date is required.");
      return;
    }
    // Business Rule 2: Pay Date > Pay Period To
    // Error if Pay Date <= Pay Period To
    if (!dayjs(values.payDate).isAfter(dayjs(values.payTo))) {
      showErrorSnackbar("Pay Date must be after the Pay Period end date.");
      return;
    }

    setSubmitting(true);
    try {
      const lockResponse = await isLock({
        corpID: corporationId,
        booksDate: values.payDate,
        type: 133,
      });

      if (
        lockResponse === true ||
        lockResponse?.IsLocked ||
        lockResponse?.Status === "Locked" ||
        String(lockResponse).toLowerCase() === "true"
      ) {
        setUnlockPasswordOpen(true);
        setSubmitting(false);
        return;
      }
    } catch (error) {
      console.error("Error checking lock status", error);
    }

    await triggerUpdateWithPcCheck(values);
  };

  handleUpdateAndNextRef.current = handleUpdateAndNext;

  useEffect(() => {
    if (!open || step !== 1 || unlockPasswordOpen) return;

    shortcutManager.addShortcut("alt+n", () => {
      if (formikRef.current && handleUpdateAndNextRef.current) {
        handleUpdateAndNextRef.current(formikRef.current.values, {
          setFieldError: formikRef.current.setFieldError,
          setFieldTouched: formikRef.current.setFieldTouched,
        });
      }
    });

    shortcutManager.addShortcut("alt+c", () => {
      onClose();
    });

    return () => {
      shortcutManager.removeShortcut("alt+n");
      shortcutManager.removeShortcut("alt+c");
    };
  }, [open, step, onClose, unlockPasswordOpen]);


  // useEffect(() => {
  //   if (!open || !corporationList?.length || !isAddPayRoll) return;
  //   let selectedOption = null;

  //   if (corpId === ALL_ID) {
  //     selectedOption =
  //       corporationList.find((c) => !c.corpValue) || null;
  //   } else if (corpId) {
  //     selectedOption =
  //       corporationList.find((c) => c.corpValue === corpId) || null;
  //   }

  //   if (!selectedOption) return;

  //   setInitialValues((prev) => ({
  //     ...prev,
  //     corporation: selectedOption,
  //   }));

  //   setCorporationId(selectedOption.corpValue || null);

  // }, [open, corpId, corporationList]);

  const isAddPayRollFlag = initialValues?.isAddPayRoll;
  const importPayrollTypeFlag = initialValues?.importPayrollType;

  useEffect(() => {
    const fetchPreviousPayroll = async () => {
      if (corporationId === ALL_ID) {
        setInitialValues((prev) => ({
          ...prev,
          corporation: {
            "corpName": "--ALL--",
            "corpValue": ""
          },
        }));
        return;
      }
      if (
        !corporationId ||
        !isAddPayRollFlag ||
        importPayrollTypeFlag === 2
      ) return;

      try {
        setSetupLoading(true);

        const payRollPrev = await PreviousPayrollByCorp(corporationId);
        const selectedCorp =
          corporationList?.find(
            (c) => String(c.corpValue) === String(corporationId)
          );

        if (payRollPrev?.StatusCode !== 200) {
          setInitialValues((prev) => ({
            ...prev,
            corporation: selectedCorp,
          }));
          return;
        }

        const data = payRollPrev?.PreviousPayroll?.[0] || {};
        handleSetupResponse(data);
        setInitialValues((prev) => ({
          ...prev,
          corporation: selectedCorp,
        }));
      } catch (error) {
        console.log("Error:", error);
      } finally {
        setSetupLoading(false);
      }
    };

    fetchPreviousPayroll();
  }, [corporationId, isAddPayRollFlag, importPayrollTypeFlag, corporationList, handleSetupResponse]);



  return (
    <Dialog open={open} fullScreen>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
        }}
      >
        {({ values, handleChange, setFieldValue, setFieldError, setFieldTouched }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <AutoGenerateName />
            <>
              {/* Show Toolbar only in Step 1 */}
              {step === 1 && (
                <Toolbar
                  sx={{
                    backgroundColor: "#F8F9FA",
                    minHeight: "37px !important",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{ flex: 1, color: "#2782EC" }}
                    component="div"
                    fontWeight={600}
                    fontSize={"16px"}
                  >
                    Payperiod Setup
                  </Typography>
                  <IconButton
                    size="small"
                    edge="start"
                    onClick={onClose}
                    style={{
                      backgroundColor: "#686868",
                      borderRadius: 2,
                      width: 20,
                      height: 20,
                    }}
                    disableRipple
                  >
                    <Close sx={{ color: "white", fontSize: "11px" }} />
                  </IconButton>
                </Toolbar>
              )}

              {/* Form Content */}
              <DialogContent
                sx={{
                  p: step === 1 ? 3 : 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {loading ? (<Loader loading={loading} />

                ) : (
                  <>
                    {loadError && (
                      enqueueSnackbar(loadError, { variant: "error" })
                    )}

                    {step === 1 && (
                      <Stack sx={{ p: 1, maxWidth: 900 }} gap={4}>
                        <Stack direction="row" gap={6}>
                          {/* Left Column */}
                          <Stack spacing={1} sx={{ width: "50%" }}>
                            {!isAddPayRoll ?
                              (
                                <FieldRow
                                  heading={requiredLabel("Corporation Name")}
                                  name="corporation"
                                  value={values.corporation}
                                  onChange={handleChange}
                                  disabled
                                  labelWidth={150}
                                />
                              ) :
                              (
                                <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
                                  <Typography fontSize={12} width={150} textAlign="left" sx={{ flexShrink: 0 }}>
                                    {requiredLabel("Corporation Name")}
                                  </Typography>
                                  <Box sx={{ width: 220, flexShrink: 0, ml: 2 }}>
                                    <Dropdown
                                      value={values.corporation || null}
                                      options={corporationList}
                                      getOptionLabel={(o) => o?.corpName || ""}
                                      isEqual={(o, v) => o?.corpValue === v?.corpValue}
                                      onChange={async (name, value) => {
                                        try {
                                          if (value?.corpValue === ALL_ID) return;
                                          setSetupLoading(true);
                                          setInitialValues(emptyValues);
                                          // setFrequencyOptions([]);
                                          setAccountOptions([]);

                                          setFieldValue("corporation", value);
                                          setCorporationId(value?.corpValue || null);
                                          setFieldValue("profitCenter", "");
                                          setFieldValue("profitCenterId", "");
                                          setFieldValue("profitCenterName", "");

                                          // const payRollPrev = await PreviousPayrollByCorp(value?.corpValue);
                                          // if (payRollPrev?.StatusCode !== 200) {
                                          //   enqueueSnackbar(`${payRollPrev?.Status}`, { variant: "warning" });
                                          //   setInitialValues({
                                          //     ...emptyValues,
                                          //     corporation: value,
                                          //   });
                                          //   return;
                                          // }
                                          // const data = payRollPrev?.PreviousPayroll?.[0] || {};
                                          // await handleSetupResponse(data);

                                          setInitialValues((prev) => ({
                                            ...prev,
                                            corporation: value,
                                          }));
                                        } catch (error) {

                                        } finally {
                                          setSetupLoading(false)
                                        }



                                      }}
                                      variant="filled"
                                      sx={{
                                        flex: 1,
                                        maxWidth: 220,
                                        backgroundColor: "white",
                                        "& .MuiFilledInput-root": {
                                          backgroundColor: "white",
                                          paddingTop: "0px",
                                          paddingBottom: "0px",
                                          height: "25px",
                                          display: "flex",
                                          alignItems: "center",
                                          "&:hover:not(.Mui-disabled)": { backgroundColor: "white" },
                                          "&.Mui-focused": { backgroundColor: "white" }
                                        },
                                      }}
                                      size="small"
                                    />
                                  </Box>
                                </Stack>
                              )
                            }

                            {(() => {
                              const isImportType2 = Number(values.importPayrollType || payrollType) === 2;
                              const isInitialFreqEmpty = !initialValues?.frequency ||
                                String(initialValues.frequency).trim() === "" ||
                                String(initialValues.frequency).toLowerCase() === "null" ||
                                !rowData?.raw?.PayFrequency ||
                                String(rowData?.raw?.PayFrequency).trim() === "" ||
                                String(rowData?.raw?.PayFrequency).toLowerCase() === "null";
                              const isCurrentFreqEmpty = !values.frequency ||
                                String(values.frequency).trim() === "" ||
                                String(values.frequency).toLowerCase() === "null";
                              const isFreqEmptyOrNull = isInitialFreqEmpty || isCurrentFreqEmpty;
                              const isImportType2WithEmptyFreq = isImportType2 && isFreqEmptyOrNull;

                              const isFrequencyDisabled = isReadOnly || (
                                !isImportType2WithEmptyFreq &&
                                (!!payrollId && rowData?.raw?.IsActive !== "Failed to import")
                              );

                              return (
                                <CustomSelect
                                  heading={requiredLabel("Pay Frequency")}
                                  name="frequency"
                                  value={values.frequency}
                                  disabled={isFrequencyDisabled}
                                  onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    setFieldValue("frequency", selectedValue);

                                    const selectedOption = frequencyOptions.find(
                                      (opt) => opt.value === selectedValue
                                    );
                                    setFieldValue(
                                      "frequencyId",
                                      selectedOption?.id || ""
                                    );
                                  }}
                                  options={frequencyOptions}
                                  labelWidth={150} // Keep this line
                                />
                              );
                            })()}


                            <CustomDatePicker
                              heading={requiredLabel("Pay Period From")}
                              name="payFrom"
                              value={values.payFrom}
                              disabled={isReadOnly}
                              setFieldValue={setFieldValue}
                              labelWidth={150}
                            />

                            <FieldRow
                              heading={requiredLabel("Payroll Name")}
                              name="payrollName"
                              value={values.payrollName}
                              onChange={handleChange}
                              labelWidth={150}
                            />
                            {values.payFrom && values.payTo && dayjs(values.payFrom).isValid() && dayjs(values.payTo).isValid() && (dayjs(values.payFrom).month() !== dayjs(values.payTo).month() || dayjs(values.payFrom).year() !== dayjs(values.payTo).year()) && (
                              <FormControlLabel
                                sx={{
                                  m: 0,
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: 12,
                                    color: "#333",
                                  },
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    name="isAccrual"
                                    checked={Boolean(values.isAccrual)}
                                    onChange={handleChange}
                                    sx={{
                                      p: "4px",
                                      "&.Mui-checked": {
                                        color: "#1976d2",
                                      },
                                    }}
                                  />
                                }
                                label="Post Accrual"
                              />
                            )}
                          </Stack>

                          {/* Right Column */}
                          <Stack spacing={1} sx={{ width: "50%" }}>
                            {profitCenterOptions && profitCenterOptions.length > 0 && (
                              <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
                                <Typography fontSize={12} width={150} textAlign="left" sx={{ flexShrink: 0 }}>
                                  Profit Center
                                </Typography>
                                <Box sx={{ width: 220, flexShrink: 0, ml: 2 }}>
                                  <Dropdown
                                    name="profitCenter"
                                    value={
                                      (() => {
                                        const isMultiple =
                                          String(values.profitCenter || "").trim().toLowerCase() === "multiple" ||
                                          String(values.profitCenterName || "").trim().toLowerCase() === "multiple";
                                        if (isMultiple) {
                                          return { label: "--Select--", value: "", id: "" };
                                        }
                                        return (
                                          (values.profitCenter && profitCenterOptions.find((opt) => opt.value === values.profitCenter || opt.label === values.profitCenter)) ||
                                          (values.profitCenterName && profitCenterOptions.find((opt) => opt.label === values.profitCenterName || opt.value === values.profitCenterName)) ||
                                          (values.profitCenterId && profitCenterOptions.find((opt) => opt.id === values.profitCenterId)) ||
                                          (values.profitCenter && values.profitCenter !== "--Select--"
                                            ? { label: values.profitCenterName || values.profitCenter, value: values.profitCenter, id: values.profitCenterId || "" }
                                            : { label: "--Select--", value: "", id: "" })
                                        );
                                      })()
                                    }
                                    disabled={isReadOnly}
                                    options={[{ label: "--Select--", value: "", id: "" }, ...profitCenterOptions]}
                                    getOptionLabel={(o) => o?.label || ""}
                                    isEqual={(o, v) => String(o?.value ?? o?.label ?? "") === String(v?.value ?? v?.label ?? "")}
                                    onChange={(name, value) => {
                                      setFieldValue("profitCenter", value ? value.value : "");
                                      setFieldValue("profitCenterName", value ? value.label : "");
                                      setFieldValue("profitCenterId", value ? (value.id || "") : "");
                                    }}
                                    variant="filled"
                                    sx={{
                                      flex: 1,
                                      maxWidth: 220,
                                      backgroundColor: isReadOnly ? "#E0E0E0 !important" : "white",
                                      "& .MuiFilledInput-root": {
                                        backgroundColor: isReadOnly ? "#E0E0E0 !important" : "white",
                                        paddingTop: "0px",
                                        paddingBottom: "0px",
                                        height: "25px",
                                        display: "flex",
                                        alignItems: "center",
                                        "&:hover:not(.Mui-disabled)": { backgroundColor: isReadOnly ? "#E0E0E0" : "white" },
                                        "&.Mui-focused": { backgroundColor: isReadOnly ? "#E0E0E0" : "white" },
                                        "&.Mui-disabled": { backgroundColor: "#E0E0E0 !important" },
                                      },
                                      "& .MuiFilledInput-input": {
                                        padding: "0 5px",
                                        fontSize: "12px",
                                        lineHeight: "1.2",
                                      },
                                    }}
                                    size="small"
                                  />
                                </Box>
                              </Stack>
                            )}
                            <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>

                              <Typography
                                fontSize={12}
                                width={150}
                                textAlign="left"
                                sx={{ flexShrink: 0 }}
                              >
                                {requiredLabel("Account")}
                              </Typography>

                              <Box sx={{ width: 220, flexShrink: 0, ml: 2 }}>
                                <Dropdown
                                  name="account"
                                  disabled={isReadOnly}
                                  options={accountOptions.filter(
                                    (opt) =>
                                      opt.type === "Other current liability" ||
                                      opt.type === "Bank" ||
                                      opt.value === values.account ||
                                      opt.value === ""
                                  )}
                                  getOptionLabel={(option) => option?.label || option?.Name || ""}
                                  isEqual={(option, value) =>
                                    String(option?.value || option?.ID || "") === String(value?.value || value?.ID || "")
                                  }
                                  renderOption={(props, option, { selected }) => (
                                    <li {...props} key={option.value || option.label}>
                                      <Box display="flex" alignItems="center" width="100%" minWidth={0}>
                                        <Typography
                                          fontSize={11}
                                          sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {option.label}
                                        </Typography>
                                        {option.type && (
                                          <Typography
                                            fontSize={11}
                                            ml={1}
                                            sx={{
                                              whiteSpace: "nowrap",
                                              flexShrink: 0,
                                              fontWeight: 600,
                                              color: selected ? "white" : "#6d6d6d",
                                            }}
                                          >
                                            {option.type}
                                          </Typography>
                                        )}
                                      </Box>
                                    </li>
                                  )}
                                  popperSx={{
                                    width: "400px !important",
                                    maxWidth: "calc(100vw - 24px)",
                                  }}
                                  value={
                                    accountOptions.find((opt) => opt.value === values.account) ||
                                    accountOptions.find((opt) => opt.value === "") ||
                                    null
                                  }
                                  onChange={(name, newValue) => {
                                    setFieldValue("account", newValue ? newValue.value : "");
                                  }}
                                  sx={{
                                    width: "100%",
                                    "& .MuiFilledInput-root": {
                                      height: "25px",
                                      minHeight: "25px",
                                      backgroundColor: isReadOnly ? "#E0E0E0 !important" : "white",
                                      paddingTop: 0,
                                      paddingBottom: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      "&:hover:not(.Mui-disabled)": { backgroundColor: isReadOnly ? "#E0E0E0" : "white" },
                                      "&.Mui-focused": { backgroundColor: isReadOnly ? "#E0E0E0" : "white" },
                                      "&.Mui-disabled": { backgroundColor: "#E0E0E0 !important" },
                                    },
                                    "& .MuiFilledInput-input": {
                                      padding: "0 5px",
                                      fontSize: "12px",
                                      lineHeight: "1.2",
                                    },
                                    "& .MuiInputBase-input.Mui-disabled": {
                                      WebkitTextFillColor: "#000 !important",
                                      color: "#000 !important",
                                    },
                                  }}
                                />
                              </Box>

                              <Typography
                                fontWeight={400}
                                fontSize={14}
                                sx={{
                                  ml: 1,
                                  pr: 1,
                                  gap: 0.5,
                                  minWidth: "auto",
                                  textAlign: "left",
                                  color: Number(values.amount || 0) >= 0 ? "green" : "red",
                                }}
                              >
                                {Number(values.amount || 0) < 0 ? "-" : ""}${Math.abs(Number(values.amount || 0)).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>

                            </Stack>




                            <CustomDatePicker
                              heading={requiredLabel("Pay Date")}
                              name="payDate"
                              value={values.payDate}
                              disabled={isReadOnly}
                              setFieldValue={setFieldValue}
                              labelWidth={150}
                            />

                            <CustomDatePicker
                              heading={requiredLabel("Pay Period To")}
                              name="payTo"
                              value={values.payTo}
                              disabled={true}
                              setFieldValue={setFieldValue}
                              labelWidth={150}
                            />

                          </Stack>
                        </Stack>
                      </Stack>
                    )}

                    {step === 2 && (
                      <Box
                        sx={{
                          width: "100%",
                          flex: 2,
                          minHeight: 0,
                          px: 1,
                          py: 1,
                          boxSizing: "border-box",
                          bgcolor: "#fff",
                        }}
                      >
                        <PayPeriodEmployeeReview
                          onClose={onClose}
                          setStep={setStep}
                          setSelectedRow={setSelectedRow}
                          accountOptions={accountOptions}
                          payrollId={payrollId}
                          corporationId={corporationId}
                          selectedRow={selectedRow}
                        />
                      </Box>
                    )}
                    {step === 4 && selectedRow && (
                      <Box
                        sx={{
                          width: "100%",
                          flex: 1,
                          minHeight: 0,
                          px: 1,
                          py: 1,
                          boxSizing: "border-box",
                          bgcolor: "#fff",
                        }}
                      >
                        <EmployeePayrollMappingForm row={selectedRow}
                          onClose={onClose}
                          setStep={setStep}
                          setSelectedRow={setSelectedRow}
                          accountOptions={accountOptions}
                          showSuccessSnackbar={showSuccessSnackbar} />
                      </Box>
                    )}

                    {/* Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 1 }}>
                      <Stack
                        direction={"row"}
                        alignItems={"flex-end"}
                        justifyContent={"flex-end"}
                        gap={2}
                      >
                        {step === 1 && (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              px: 3,
                              py: 0.75,
                              fontSize: 12,
                              backgroundColor: "#e8e8e8",
                              color: "#000",
                              "&:hover": { backgroundColor: "#dedede" },
                            }}
                            onClick={onClose}
                          >
                            <u>C</u>ancel
                          </Button>
                        )}
                        {step === 1 && (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              px: 3,
                              py: 0.75,
                              fontSize: 12,
                              backgroundColor: "#b5dcf2",
                              color: "#000",
                              "&:hover": { backgroundColor: "#a7d2ea" },
                            }}
                            onClick={() => handleUpdateAndNext(values, { setFieldError, setFieldTouched })}
                          >
                            {`${isAddPayRoll ? "Save & Next" : "Update & Next"}`}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </>
                )}
              </DialogContent>
            </>
            <UnlockDialog
              open={unlockPasswordOpen}
              onClose={() => {
                setUnlockPasswordOpen(false);
              }}
              onSuccess={() => {
                setUnlockPasswordOpen(false);
                triggerUpdateWithPcCheck(values);
              }}
              corporationId={corporationId}
            />
            <PayrollsPopup
              open={pcConfirmOpen}
              popupType="confirm"
              confirmText="Yes"
              cancelText="No"
              message="Profit Center has been Updated For all Employee In this Payroll. Do you want to update the Profit Center?"
              onConfirm={() => {
                setPcConfirmOpen(false);
                if (pendingUpdateValues) {
                  performUpdate(pendingUpdateValues, true);
                }
              }}
              onCancel={() => {
                setPcConfirmOpen(false);
                if (pendingUpdateValues) {
                  performUpdate(pendingUpdateValues, false);
                }
              }}
              onDismiss={() => {
                setPcConfirmOpen(false);
                setPendingUpdateValues(null);
              }}
            />
          </LocalizationProvider>
        )}
      </Formik>
    </Dialog>
  );
};

export default PayPeriodSetup;
